import React from 'react';
import PropTypes from 'prop-types';
import { CustomProvider, Loading } from '@leegality/leegality-react-component-library';
import { QueryClientProvider } from '@tanstack/react-query';
import ErrorBoundary from '@pages/error/error-boundary';
import { queryClient } from '@lib';
import useCustomFont from '@hooks/use-custom-font';
import { Toaster as Notification } from 'react-hot-toast';

// Dev-only: React Query devtools must never ship to production. Guarded by import.meta.env.DEV
// so Vite dead-code-eliminates the dynamic import from prod builds.
const ReactQueryDevtools = import.meta.env.DEV
  ? React.lazy(() => import('@tanstack/react-query-devtools').then((m) => ({ default: m.ReactQueryDevtools })))
  : null;

const AppProvider = ({ children }) => {
  // Resolved from the fetched profile; CustomProvider re-applies when it changes.
  // fontFamily sets --font-family-base; fontFaces registers CDN font files via
  // the library's FontFace loader so the name actually renders.
  const { fontFamily, fontFaces } = useCustomFont();
  const themeConfig = React.useMemo(
    () => ({ fontFamily, fontFaces }),
    [fontFamily, fontFaces],
  );

  return (
    <React.Suspense fallback={<Loading />}>
      <ErrorBoundary>
        <CustomProvider themeConfig={themeConfig}>
          <QueryClientProvider client={queryClient}>
            <Notification />
            {children}
            {ReactQueryDevtools && (
              <React.Suspense fallback={null}>
                <ReactQueryDevtools />
              </React.Suspense>
            )}
          </QueryClientProvider>
        </CustomProvider>
      </ErrorBoundary>
    </React.Suspense>
  );
};

AppProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AppProvider;
